<template>
    <UModal
        v-model:open="open"
        :title="title"
        :ui="{ content: 'mx-4 w-full sm:w-2/3 md:w-1/2 lg:w-2/5 max-w-none' }"
        @update:open="(value: boolean) => { if (!value) resolveInstanceTypePick(null) }"
    >
        <template #body>
            <!-- With the account section below, each half says what it is; on its own the
                 dialog's own title already said it. -->
            <p v-if="options.withAccount" class="mb-2 text-xs font-medium text-muted">
                {{ $t('instance_type.title') }}
            </p>

            <URadioGroup
                v-model="selected"
                :items="items"
                :ui="{ fieldset: 'w-full gap-2', item: 'w-full' }"
                variant="card"
            >
                <template #label="{ item }">
                    <span class="flex items-center justify-between gap-2">
                        <span class="truncate font-medium text-default">{{ item.label }}</span>
                        <UTooltip v-if="item.full" :text="$t('instance_type.limit_reached')">
                            <UBadge color="neutral" :label="item.cap" size="sm" variant="subtle" />
                        </UTooltip>
                        <UBadge
                            v-else-if="item.cap"
                            color="neutral"
                            :label="item.cap"
                            size="sm"
                            variant="subtle"
                        />
                    </span>
                </template>

                <template #description="{ item }">
                    <InstanceTypeSummary
                        :cpu="item.cpu"
                        :memory-bytes="item.memoryBytes"
                        :ws-size-bytes="item.wsSizeBytes"
                    />
                </template>
            </URadioGroup>

            <!-- The second question of a start, for somebody who keeps more than one Claude
                 login: same dialog, one Start button, nothing to answer twice. -->
            <template v-if="options.withAccount">
                <p class="mb-2 mt-5 text-xs font-medium text-muted">{{ $t('claude_account.one') }}</p>

                <USelect
                    v-model="account"
                    class="w-full"
                    icon="i-lucide-sparkles"
                    :items="accountOptions"
                    value-key="value"
                />
            </template>

            <div class="flex justify-end gap-2 mt-5">
                <UButton
                    color="neutral"
                    :label="$t('general.cancel')"
                    variant="subtle"
                    @click="resolveInstanceTypePick(null)"
                />
                <UButton
                    :disabled="!selected"
                    :icon="options.confirmIcon"
                    :label="options.confirmLabel"
                    @click="confirm"
                />
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import InstanceTypeSummary from '@/components/InstanceTypeSummary.vue'
    import { claudeAccounts, defaultClaudeAccount } from '@/libs/claudeAccount'
    import {
        allowedInstanceTypes,
        instanceTypeAtCap,
        instanceTypePickerOpen,
        instanceTypePickerOptions,
        preselectedInstanceType,
        rememberInstanceType,
        resolveInstanceTypePick,
    } from '@/libs/instanceType'
    import type { SelectOption } from '@/types/Common'

    // One card per allowed type. `cap` is an empty string for an unlimited type, which is
    // what keeps the badge off it: a number nobody can reach is not worth a chip.
    interface TypeItem {
        value:       string
        label:       string
        cpu:         number
        memoryBytes: number
        wsSizeBytes: number
        cap:         string
        full:        boolean
        disabled:    boolean
    }

    const { t } = useI18n()

    const open     = instanceTypePickerOpen
    const options  = instanceTypePickerOptions
    const selected = ref('')
    const account  = ref('')

    // Two questions in one dialog is a start, not a choice of size, and the heading says so.
    const title = computed(() => options.value.withAccount
        ? t('instance_type.start_title')
        : t('instance_type.choose'))

    const items = computed((): TypeItem[] => allowedInstanceTypes().map(type => ({
        value:       type.id,
        label:       type.name,
        cpu:         type.cpu,
        memoryBytes: type.memory_bytes,
        wsSizeBytes: type.ws_size_bytes,
        cap:         type.max_running === -1 ? '' : `${type.running}/${type.max_running}`,
        full:        instanceTypeAtCap(type),
        // A type that is already full cannot be started again — but the one the workspace
        // already runs at stays selectable, or a change dialog could not open on it.
        disabled:    instanceTypeAtCap(type) && type.id !== options.value.currentId,
    })))

    const accountOptions = computed((): SelectOption[] =>
        claudeAccounts().map(item => ({ label: item.label, value: item.id })))

    // The picker opens on what a start would have used anyway: the project's last choice,
    // then the current type, then the default.
    watch(open, (value) => {
        if (!value) return

        const wanted = preselectedInstanceType(options.value.projectGitLabId, options.value.currentId ?? '')

        // A remembered or default type that is already full would open the dialog on a card
        // the plane would refuse, so the first one that can actually be started wins instead.
        const usable = items.value.find(item => item.value === wanted && !item.disabled)

        selected.value = usable?.value ?? items.value.find(item => !item.disabled)?.value ?? ''
        account.value  = defaultClaudeAccount()?.id ?? ''
    })

    const confirm = () => {
        if (!selected.value) return

        rememberInstanceType(options.value.projectGitLabId, selected.value)
        resolveInstanceTypePick({
            typeId:    selected.value,
            accountId: options.value.withAccount ? account.value : '',
        })
    }
</script>
